import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useParams, useNavigate } from 'react-router-dom';

interface DaySchedule {
  open: string;
  close: string;
  isClosed?: boolean;
  is24h?: boolean;
  lunchBreak?: {
    start: string;
    end: string;
  };
}

interface BusinessHours {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
  isOnlineStore?: boolean;
}

interface Banner {
  imageUrl: string;
  linkUrl: string;
  title: string;
}

interface StoreSettings {
  storeId: string;
  slug: string;
  storeName: string;
  description: string;
  businessHours: BusinessHours;
  banners: Banner[];
  colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
  vat: string;
  address: string;
  commercialEmail: string;
  commercialPhone: string;
  district: string;
}

export function useStore() {
  const [settings, setSettings] = useState<StoreSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { storeSlug } = useParams<{ storeSlug: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    async function loadStoreSettings() {
      if (!storeSlug) {
        setError('Store slug not found');
        setLoading(false);
        return;
      }

      try {
        // First, find the store document by slug
        const storesRef = collection(db, 'stores');
        const q = query(storesRef, where('slug', '==', storeSlug));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          console.error('Store not found:', storeSlug);
          setError('Store not found');
          setLoading(false);
          return;
        }

        const storeDoc = querySnapshot.docs[0];
        const storeData = storeDoc.data();

        setSettings({
          storeId: storeDoc.id,
          slug: storeData.slug,
          storeName: storeData.storeName || 'Loja Online',
          description: storeData.description || '',
          businessHours: storeData.businessHours || {
            monday: { open: '09:00', close: '18:00' },
            tuesday: { open: '09:00', close: '18:00' },
            wednesday: { open: '09:00', close: '18:00' },
            thursday: { open: '09:00', close: '18:00' },
            friday: { open: '09:00', close: '18:00' },
            saturday: { open: '09:00', close: '13:00' },
            sunday: { isClosed: true, open: '', close: '' },
            isOnlineStore: false
          },
          banners: storeData.banners || [],
          colors: storeData.colors || {
            primary: '#4F46E5',
            secondary: '#1F2937',
            accent: '#EF4444'
          },
          vat: storeData.vat || '',
          address: storeData.address || '',
          commercialEmail: storeData.commercialEmail || '',
          commercialPhone: storeData.commercialPhone || '',
          district: storeData.district || ''
        });
      } catch (err) {
        console.error('Error loading store settings:', err);
        setError('Error loading store settings');
      } finally {
        setLoading(false);
      }
    }

    loadStoreSettings();
  }, [storeSlug, navigate]);

  return { settings, loading, error };
}
